import { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import TextField from "@/components/TextField";
import Icon from "@/components/Icon";
import ListItem from "@/components/ListItem/ListItem";
import SubSectionHeading from "@/components/SubSectionHeading";
import Banner from "@/components/Banner";
import { toFormat } from "@/lib/dateHelpers";
import type { CRMNoteReference } from "@/types";
import { isProfileDocumentHiddenFromViewer } from "@/features/person/components/PersonProfileDocumentFields";
import { PERSON_PROFILE_DOCUMENTS_QUERY } from "@/features/person/queries";
import { useCanViewPersonDocumentsBool } from "@/hooks/useCapabilities";
import { getReferenceTypeLabel, getReferenceIcon } from "../ReferencesTable/types";

export interface UploadedFileEntry {
  file: File;
  selected: boolean;
  name: string;
  description: string;
  category: string;
  privacyRestricted: boolean;
}

interface ReferenceAdderProps {
  pgaId: string;
  selectedReferences: CRMNoteReference[];
  onSelectionChange: (references: CRMNoteReference[]) => void;
  uploadedFiles?: UploadedFileEntry[];
  onFileSelectedChange?: (index: number, selected: boolean) => void;
}
export default function ReferenceAdder({
  pgaId,
  selectedReferences,
  onSelectionChange,
  uploadedFiles = [],
  onFileSelectedChange
}: ReferenceAdderProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const canViewPersonDocuments = useCanViewPersonDocumentsBool();

  const {
    data,
    loading: documentsLoading,
    error: documentsError
  } = useQuery(PERSON_PROFILE_DOCUMENTS_QUERY, {
    variables: { pgaId },
    skip: !pgaId || !canViewPersonDocuments,
    errorPolicy: "all"
  });

  const allDocuments: CRMNoteReference[] = useMemo(
    () => (data?.person?.profileDocuments ?? []).filter((doc) => !isProfileDocumentHiddenFromViewer(doc)),
    [data]
  );

  const selectedIds = useMemo(() => new Set(selectedReferences.map((r) => r.id)), [selectedReferences]);

  const availableDocuments = useMemo(() => {
    const unselected = allDocuments.filter((doc) => !selectedIds.has(doc.id));
    if (!searchTerm.trim()) return unselected;
    const term = searchTerm.toLowerCase();
    return unselected.filter(
      (doc) =>
        doc.name?.toLowerCase().includes(term) ||
        doc.filename?.toLowerCase().includes(term) ||
        doc.category?.toLowerCase().includes(term)
    );
  }, [allDocuments, selectedIds, searchTerm]);

  const visibleSelectedReferences = useMemo(
    () => selectedReferences.filter((ref) => !isProfileDocumentHiddenFromViewer(ref)),
    [selectedReferences]
  );

  const selectedFileCount = uploadedFiles.filter((f) => f.selected).length;
  const unselectedFiles = uploadedFiles.filter((f) => !f.selected);
  const selectedCount = visibleSelectedReferences.length + selectedFileCount;

  const handleSelect = (doc: CRMNoteReference) => {
    const docWithTypename: CRMNoteReference = {
      ...doc,
      __typename: doc.__typename || "BackofficePersonProfileDocument"
    };
    onSelectionChange([...selectedReferences, docWithTypename]);
  };

  const handleUncheck = (docId: string) => {
    onSelectionChange(selectedReferences.filter((r) => r.id !== docId));
  };

  const formatDocDate = (doc: CRMNoteReference) => {
    const date = doc.updatedAt || doc.createdAt;
    if (!date) return "";
    return toFormat(date, "MMM dd, yyyy") || "";
  };

  const trailingIcon = searchTerm ? (
    <button
      type="button"
      className="cursor-pointer flex items-center h-full"
      onClick={() => setSearchTerm("")}
      aria-label="Clear document search"
    >
      <Icon name="cancel" />
    </button>
  ) : undefined;

  return (
    <div>
      {documentsError && !documentsLoading && (
        <Banner
          variant="error"
          testId="reference-query-error"
          description="Could not load documents. You can still save the note without documents."
          className="mb-4"
        />
      )}

      <div data-testid="reference-autocomplete">
        <TextField
          label="Search for a Document"
          value={searchTerm}
          onChangeText={setSearchTerm}
          leadingIcon="search"
          trailingIcon={trailingIcon}
          variant="outlined"
          className="w-full"
        />
      </div>

      {selectedCount > 0 && (
        <div className="sticky -top-px z-10 mt-4 pt-px pb-2 bg-sys-brand-surface-container-low">
          <SubSectionHeading>Selected ({selectedCount})</SubSectionHeading>
        </div>
      )}

      {selectedCount > 0 && (
        <div className="space-y-1" data-testid="selected-references-list">
          {onFileSelectedChange &&
            uploadedFiles.map(
              (entry, index) =>
                entry.selected && (
                  <ListItem
                    key={`upload-${index}`}
                    compact
                    useAvatar
                    iconName="upload_file"
                    overline="File Upload"
                    headline={entry.name || entry.file.name}
                    useCheckbox
                    checkboxChecked={true}
                    onCheckboxChange={() => onFileSelectedChange(index, false)}
                    checkboxName={`uploaded-file-${index}-selected`}
                  />
                )
            )}
          {visibleSelectedReferences.map((ref) => (
            <ListItem
              key={ref.id}
              compact
              useAvatar
              iconName={getReferenceIcon(ref.__typename)}
              overline={getReferenceTypeLabel(ref.__typename)}
              headline={ref.filename || ref.name || "--"}
              supportingText={formatDocDate(ref)}
              useCheckbox
              checkboxChecked={true}
              onCheckboxChange={() => handleUncheck(ref.id)}
              checkboxName={`reference-${ref.id}-selected`}
            />
          ))}
        </div>
      )}

      <div className="mt-4">
        {documentsLoading ? (
          <div className="py-3 text-center text-sys-brand-on-surface-variant">Loading...</div>
        ) : availableDocuments.length === 0 && unselectedFiles.length === 0 ? (
          <div className="py-3 px-4 text-center text-body-medium-semibold text-extended-pgasilver-brand-on-color bg-extended-pgasilver-brand-color rounded-lg">
            {searchTerm.trim()
              ? "No matching documents found"
              : allDocuments.length === 0
                ? "No documents found for this person"
                : "All available documents have been selected"}
          </div>
        ) : (
          <>
            <SubSectionHeading className="mb-2">Available</SubSectionHeading>
            {onFileSelectedChange &&
              uploadedFiles.map(
                (entry, index) =>
                  !entry.selected && (
                    <ListItem
                      key={`upload-${index}`}
                      compact
                      useAvatar
                      iconName="upload_file"
                      overline="File Upload"
                      headline={entry.name || entry.file.name}
                      useCheckbox
                      checkboxChecked={false}
                      onCheckboxChange={() => onFileSelectedChange(index, true)}
                      checkboxName={`uploaded-file-${index}-available`}
                    />
                  )
              )}
            {availableDocuments.map((doc) => (
              <ListItem
                key={doc.id}
                compact
                useAvatar
                iconName={getReferenceIcon(doc.__typename)}
                overline={getReferenceTypeLabel(doc.__typename)}
                headline={doc.filename || doc.name || "--"}
                supportingText={formatDocDate(doc)}
                useCheckbox
                checkboxChecked={false}
                onCheckboxChange={() => handleSelect(doc)}
                checkboxName={`reference-${doc.id}-available`}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
